import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { HouseholdProvider, useHousehold } from '@/context/HouseholdContext'
import { I18nProvider, useT } from '@/i18n/I18nProvider'
import { ConfirmProvider } from '@/context/ConfirmProvider'
import { VersionWatcher } from '@/components/VersionWatcher'
import { LoginPage } from '@/pages/LoginPage'
import { UsersPage } from '@/pages/UsersPage'
import { PlatformUsersPage } from '@/pages/PlatformUsersPage'
import { AppLayout } from '@/components/layout/AppLayout'
import { DashboardPage } from '@/pages/DashboardPage'
import { AccountsPage } from '@/pages/AccountsPage'
import { AccountStatementPage } from '@/pages/AccountStatementPage'
import { TransactionsPage } from '@/pages/TransactionsPage'
import { CardsPage } from '@/pages/CardsPage'
import { CardStatementPage } from '@/pages/CardStatementPage'
import { RecurringPage } from '@/pages/RecurringPage'
import { BudgetPage } from '@/pages/BudgetPage'
import { NetWorthPage } from '@/pages/NetWorthPage'
import { ReportsPage } from '@/pages/ReportsPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { CategoriesPage } from '@/pages/CategoriesPage'
import { TagsPage } from '@/pages/TagsPage'
import { CurrencySettingsPage } from '@/pages/CurrencySettingsPage'
import { HouseholdSettingsPage } from '@/pages/HouseholdSettingsPage'
import { isSupabaseConfigured } from '@/lib/supabase'

function ConfigNotice() {
  return (
    <div className="mx-auto mt-20 max-w-md rounded-lg border border-amber-500/40 bg-amber-500/10 p-6 text-center">
      <h1 className="mb-2 text-lg font-semibold">Configure o Supabase</h1>
      <p className="text-muted-foreground text-sm">
        Copie <code className="rounded bg-black/10 px-1">.env.example</code> para{' '}
        <code className="rounded bg-black/10 px-1">.env</code> e preencha{' '}
        <code className="rounded bg-black/10 px-1">VITE_SUPABASE_URL</code> e{' '}
        <code className="rounded bg-black/10 px-1">VITE_SUPABASE_ANON_KEY</code>, depois reinicie o dev server.
      </p>
    </div>
  )
}

// Segura a renderização das telas até o TENANT ativo resolver — evita disparar
// queries com household_id vazio (que voltam 400) no intervalo do load.
function HouseholdGate({ children }: { children: React.ReactNode }) {
  const { loading, householdId } = useHousehold()
  const { t } = useT()
  if (loading)
    return (
      <div className="text-muted-foreground flex min-h-svh items-center justify-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
      </div>
    )
  if (!householdId)
    return (
      <div className="text-muted-foreground flex min-h-svh items-center justify-center px-6 text-center text-sm">
        {t('app.noTenant')}
      </div>
    )
  return <>{children}</>
}

export default function App() {
  const { session, loading } = useAuth()
  const qc = useQueryClient()

  // Ao sair (ou expirar), zera o cache: nenhum dado financeiro fica no ar para
  // o próximo que abrir a aba.
  useEffect(() => {
    if (!session) qc.clear()
  }, [session, qc])

  if (!isSupabaseConfigured) return <ConfigNotice />
  if (loading)
    return (
      <div className="text-muted-foreground flex min-h-svh items-center justify-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </div>
    )
  if (!session) return <LoginPage />

  return (
    <HouseholdProvider>
    <I18nProvider>
    <ConfirmProvider>
    <VersionWatcher />
    <HouseholdGate>
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="contas" element={<AccountsPage />} />
        <Route path="contas/:id" element={<AccountStatementPage />} />
        <Route path="transacoes" element={<TransactionsPage />} />
        <Route path="cartoes" element={<CardsPage />} />
        <Route path="cartoes/:id" element={<CardStatementPage />} />
        <Route path="recorrentes" element={<RecurringPage />} />
        <Route path="orcamento" element={<BudgetPage />} />
        <Route path="patrimonio" element={<NetWorthPage />} />
        <Route path="relatorios" element={<ReportsPage />} />
        <Route path="configuracoes" element={<SettingsPage />}>
          <Route index element={<Navigate to="casa" replace />} />
          <Route path="casa" element={<HouseholdSettingsPage />} />
          <Route path="categorias" element={<CategoriesPage />} />
          <Route path="tags" element={<TagsPage />} />
          <Route path="moeda" element={<CurrencySettingsPage />} />
          <Route path="usuarios" element={<UsersPage />} />
          <Route path="plataforma" element={<PlatformUsersPage />} />
        </Route>
      </Route>
    </Routes>
    </HouseholdGate>
    </ConfirmProvider>
    </I18nProvider>
    </HouseholdProvider>
  )
}
